// @react-compiler enable
import {getSessionStatusIcon} from '@github-ui/agent-sessions/components/StatusIcon'
import {SessionState} from '@github-ui/agent-sessions/types/session'
import {generateTaskPathForRepo} from '@github-ui/agent-sessions/utils/generate-task-path'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {testIdProps} from '@github-ui/test-id-props'
import {CopilotIcon} from '@primer/octicons-react'
import {Link, RelativeTime} from '@primer/react'
import {memo, useEffect, useMemo, useRef} from 'react'

import type {AgentInfo, AgentTask} from '../../api/memex-items/contracts'
import {useTableCopilotTasks} from '../../features/copilot-tasks/use-table-copilot-tasks'
import {isCopilot} from '../../helpers/assignee-helper'
import type {MemexItemModel} from '../../models/memex-item-model'
import styles from './agent-task-sub-row.module.css'
import {shouldShowAgentTaskPill} from './agent-task-utils'
import {focusAgentTaskRow, TableFocusType, useStableTableNavigation, useTableNavigation} from './navigation'

interface AgentTaskSubRowProps {
  model: MemexItemModel
  /** Individual task data to render. */
  task: AgentTask
  /** TanStack row ID of the parent row, used for table navigation focus tracking. */
  rowId?: string
  /** Hierarchy depth of the parent row (0 for top-level items). */
  depth?: number
  /** Whether the table hierarchy (Show hierarchy toggle) is enabled. */
  hierarchyEnabled?: boolean
  /** Total width (px) of visible columns before the Title column, excluding the drag handle. */
  titleColumnOffset?: number
  /** Whether this is the last sub-row in the group (controls connector line style). */
  isLast?: boolean
}

function AgentTaskAgentIcon({agent}: {agent?: AgentInfo}) {
  const isCopilotAgent = agent ? isCopilot(agent.login) : false
  const hasAvatar = !!agent?.avatarUrl
  const altText = agent?.displayName || agent?.login

  if (agent && !isCopilotAgent && hasAvatar && altText) {
    return <GitHubAvatar src={agent.avatarUrl} alt={altText} size={16} />
  }

  return <CopilotIcon size={16} />
}

export const AgentTaskSubRow = memo(function AgentTaskSubRow({
  model,
  task,
  rowId,
  depth = 0,
  hierarchyEnabled = false,
  titleColumnOffset = 0,
  isLast = true,
}: AgentTaskSubRowProps) {
  const {copilotTasksEnabled} = useTableCopilotTasks()
  const linkRef = useRef<HTMLAnchorElement>(null)
  const {state} = useTableNavigation()
  const {navigationDispatch} = useStableTableNavigation()

  const taskId = task.id
  const taskUrl = useMemo(() => {
    if (!taskId) return undefined
    const repositoryNwo = task.repoNwo ?? (model.getNameWithOwner()?.join('/') || undefined)
    return generateTaskPathForRepo({taskId, repositoryNwo}) || undefined
  }, [taskId, task.repoNwo, model])

  // Focus the link when table navigation state targets this agent task row
  useEffect(() => {
    if (
      state.focus?.type === TableFocusType.AGENT_TASK_ROW &&
      state.focus.details?.parentRowId === rowId &&
      state.focus.details?.taskId === taskId
    ) {
      linkRef.current?.focus()
    }
  }, [state.focus, rowId, taskId])

  if (!copilotTasksEnabled) return null
  if (!shouldShowAgentTaskPill(task.state)) return null
  const isQueued = task.state === SessionState.Queued
  const statusIcon = getSessionStatusIcon(task.state)

  return (
    <div
      className={styles.AgentTaskSubRow}
      style={
        {
          '--agent-task-depth': depth,
          '--hierarchy-enabled': hierarchyEnabled ? 1 : 0,
          '--title-column-offset': titleColumnOffset,
        } as React.CSSProperties
      }
      {...testIdProps('agent-task-sub-row')}
    >
      <span
        className={`${styles.connector} ${isLast ? styles.connectorLast : styles.connectorMiddle}`}
        aria-hidden="true"
      />
      <div className={styles.contentWrapper}>
        <span className={styles.statusIcon}>{statusIcon}</span>
        <span className={styles.agentIcon}>
          <AgentTaskAgentIcon agent={task.agent} />
        </span>
        {task.title &&
          (taskUrl ? (
            <Link
              ref={linkRef}
              href={taskUrl}
              target="_blank"
              rel="noreferrer"
              className={styles.title}
              onFocus={() => rowId && navigationDispatch(focusAgentTaskRow(rowId, taskId))}
            >
              {task.title}
            </Link>
          ) : (
            <span className={styles.titleMuted}>{task.title}</span>
          ))}
        {isQueued && task.createdAt ? (
          <span className={styles.elapsedTime}>
            created <RelativeTime date={new Date(task.createdAt)} />
          </span>
        ) : (
          task.lastUpdatedAt && (
            <span className={styles.elapsedTime}>
              updated <RelativeTime date={new Date(task.lastUpdatedAt)} />
            </span>
          )
        )}
      </div>
    </div>
  )
})
